import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { ClaimEvaluationRow, ClaimEvaluationData } from './ClaimEvaluationRow';
import type { ReferenceMetadata, MetadataValidation } from '../api';

const CardContainer = styled.div`
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-md);
  background: var(--background-white);
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--stroke-inactive);
  }

  &.streaming {
    border-color: var(--brand-colors-blue-300);
    box-shadow: 0 2px 8px rgba(6, 69, 177, 0.08);
  }
`;

const CardHeader = styled('div')<{ clickable: boolean }>`
  padding: var(--spacing-sm-24);
  transition: background-color 0.15s ease;

  &.clickable {
    cursor: pointer;

    &:hover {
      background: var(--brand-colors-neutral-200);
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-8);
  margin-bottom: var(--spacing-xs-8);
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  flex-wrap: wrap;
`;

const SummaryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  padding: 6px 12px;
  border-radius: var(--border-radius-radius-sm);
  font-size: 14px;
  font-weight: 500;
  background: var(--brand-colors-neutral-200);
  color: var(--text-secondary);
  border: 1px solid var(--stroke-light);
`;

const ClaimsSection = styled('div')<{ expanded: boolean }>`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
  border-top: none;

  &.expanded {
    max-height: 2000px;
    border-top: 1px solid var(--stroke-light);
  }
`;

const ClaimsContainer = styled.div`
  padding: var(--spacing-sm-24);
  background: var(--brand-colors-neutral-200);
`;

const ClaimsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-xs-16);
`;

const NoClaimsMessage = styled(Body)`
  color: var(--text-inactive);
  font-style: italic;
`;

const SkeletonClaimRow = styled.div`
  padding: var(--spacing-xs-12);
  background: var(--background-white);
  border: 1px solid var(--stroke-inactive);
  border-radius: var(--border-radius-radius-sm);
  margin-bottom: var(--spacing-xs-8);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SkeletonLine = styled.div`
  height: 14px;
  background: linear-gradient(
    90deg,
    var(--brand-colors-neutral-300) 25%,
    var(--brand-colors-neutral-200) 50%,
    var(--brand-colors-neutral-300) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusBadge = styled('div')<{ status: ReferenceStatus }>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  padding: 6px 12px;
  border-radius: var(--border-radius-radius-sm);
  font-size: 14px;
  font-weight: 600;
  border: 1px solid;

  &.status-no_issues {
    background: var(--brand-colors-success-100);
    color: var(--brand-colors-success-900);
    border-color: var(--brand-colors-success-500);
  }

  &.status-potential_issues {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }

  &.status-issues {
    background: var(--brand-colors-error-100);
    color: var(--brand-colors-error-900);
    border-color: var(--brand-colors-error-500);
  }

  &.status-loading {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }

  &.status-error {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }
`;

const SeeDetailsLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  color: var(--text-link);
  font-size: 15px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--brand-colors-blue-700);
  }
`;

const ReferenceText = styled.div`
  font-size: 28px;
  line-height: 1.5;
  color: var(--text-primary);
  margin-top: var(--spacing-xs-12);
  font-weight: 400;
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8);
  align-items: center;
  margin-top: var(--spacing-xs-12);
`;

const InfoBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  padding: 2px 8px;
  background: var(--brand-colors-neutral-200);
  border: 1px solid var(--stroke-light);
  border-radius: var(--border-radius-radius-sm);
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
  cursor: default;

  &[href] {
    cursor: pointer;
    color: var(--text-link);

    &:hover {
      background: var(--brand-colors-blue-100);
      border-color: var(--brand-colors-blue-300);
      color: var(--brand-colors-blue-700);
      text-decoration: none;
    }
  }
`;

const JournalInfo = styled.div`
  margin-top: var(--spacing-xs-8);
  font-size: 12px;
  color: var(--text-secondary);
`;

const MetadataSection = styled.div`
  margin-bottom: var(--spacing-sm-24);
  padding-bottom: var(--spacing-sm-24);
  border-bottom: 1px solid var(--stroke-light);
`;

const MetadataValidationRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  margin-top: var(--spacing-xs-8);
`;

const ValidationBadge = styled('div')<{
  status: 'pass' | 'fail' | 'warning' | 'neutral';
}>`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  padding: 2px 8px;
  border-radius: var(--border-radius-radius-sm);
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;

  &.status-pass {
    background: var(--brand-colors-success-100);
    color: var(--brand-colors-success-900);
    border-color: var(--brand-colors-success-500);
  }

  &.status-fail {
    background: var(--brand-colors-error-100);
    color: var(--brand-colors-error-900);
    border-color: var(--brand-colors-error-500);
  }

  &.status-warning {
    background: #fef3c7;
    color: #92400e;
    border-color: #fbbf24;
  }

  &.status-neutral {
    background: var(--brand-colors-neutral-200);
    color: var(--text-secondary);
    border-color: var(--stroke-light);
  }
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  gap: var(--spacing-xs-8) var(--spacing-xs-16);
  margin-top: var(--spacing-xs-12);
  font-size: 13px;
  background: var(--background-white);
  padding: var(--spacing-xs-12);
  border-radius: var(--border-radius-radius-sm);
  border: 1px solid var(--stroke-light);
`;

const MetadataLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 600;
  padding: 4px 8px;
`;

const MetadataValue = styled('span')<{
  status?: 'match' | 'mismatch' | 'none';
}>`
  color: var(--text-primary);
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.15s ease;

  &.status-mismatch {
    background-color: var(--brand-colors-error-100);
    color: var(--brand-colors-error-900);
    font-weight: 500;
  }

  &.status-match {
    background-color: var(--brand-colors-success-100);
    color: var(--brand-colors-success-900);
  }
`;

const MetadataHeader = styled.div`
  display: contents;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export type ReferenceCardProps = {
  id: string;
  identifier?: string | null;
  title: string;
  authors: string[];
  publicationYear?: number | null;
  publication?: string | null;
  doi?: string | null;
  citationCount?: number | null;
  impactFactor?: number | null;
  journalQuartile?: string | null;
  claims: string[];
  evaluations?: ClaimEvaluationData[];
  originalText?: string;
  isStreaming?: boolean;
  fullTextUrl?: string | null;
  bestSourceUrl?: string | null;
  openalexId?: string | null;
  openalexUrl?: string | null;
  rawReference?: string | null;
  foundMetadata?: ReferenceMetadata;
  metadataSource?: 'openalex' | 'web';
  validation?: MetadataValidation;
};

type ReferenceStatus =
  | 'no_issues'
  | 'potential_issues'
  | 'issues'
  | 'loading'
  | 'error';

function getValidationStatus(
  validationResult: boolean | null | undefined,
): 'match' | 'mismatch' | 'none' {
  if (validationResult === false) return 'mismatch';
  if (validationResult) return 'match';
  return 'none';
}

function getDoiStatus(
  citationDoi: string | null | undefined,
  foundDoi: string | null | undefined,
  doiMatchResult?: boolean | null,
): 'match' | 'mismatch' | 'none' {
  if (!citationDoi || !foundDoi) return 'none';
  if (doiMatchResult != null) return doiMatchResult ? 'match' : 'mismatch';
  return citationDoi === foundDoi ? 'match' : 'mismatch';
}

function calculateReferenceIssues(
  evaluations: ClaimEvaluationData[],
  claims: string[],
  isStreaming: boolean,
  openalexId?: string | null,
  fullTextUrl?: string | null,
  bestSourceUrl?: string | null,
  validation?: MetadataValidation | null,
): { status: ReferenceStatus; issues: string[] } {
  const canVerifyPaper = openalexId || fullTextUrl || bestSourceUrl;
  const issues: string[] = [];

  // Check for metadata validation issues
  const hasMetadataIssues =
    validation != null &&
    (validation.title_similarity_threshold_met === false ||
      validation.authors_match === false ||
      validation.year_matches === false ||
      validation.publication_matches === false ||
      validation.doi_format_valid === false ||
      validation.doi_resolves === false ||
      validation.doi_matches === false);

  if (hasMetadataIssues) {
    issues.push('Metadata issues');
  }

  // Only show paper verification if there's an issue
  if (!canVerifyPaper) {
    issues.push("Can't find paper");
  }

  // If no claims to check
  if (claims.length === 0) {
    if (!canVerifyPaper) return { status: 'issues', issues };
    if (hasMetadataIssues) return { status: 'potential_issues', issues };
    return { status: 'no_issues', issues };
  }

  // Check for loading state
  const pendingCount = claims.length - evaluations.length;
  if (isStreaming && pendingCount > 0) {
    issues.push(`Checking ${pendingCount} of ${claims.length} claims`);
    return { status: 'loading', issues };
  }

  // Check if we have evaluations
  if (evaluations.length === 0) {
    issues.push('No evaluations available');
    return { status: 'error', issues };
  }

  // Count each type of issue
  const notSupportedCount = evaluations.filter(
    (e) => e.supported === 'not_supported',
  ).length;
  const partiallySupportedCount = evaluations.filter(
    (e) => e.supported === 'partially_supported',
  ).length;
  const maybeSupportedCount = evaluations.filter(
    (e) => e.supported === 'maybe_supported',
  ).length;
  const couldNotFindCount = evaluations.filter(
    (e) => e.supported === 'could_not_find_support',
  ).length;
  const supportedCount = evaluations.filter(
    (e) => e.supported === 'supported',
  ).length;

  // Add individual issue badges
  if (notSupportedCount > 0) {
    issues.push(
      `${notSupportedCount} claim${notSupportedCount > 1 ? 's' : ''} not supported`,
    );
  }
  if (partiallySupportedCount > 0) {
    issues.push(
      `${partiallySupportedCount} claim${partiallySupportedCount > 1 ? 's' : ''} partially supported`,
    );
  }
  if (maybeSupportedCount > 0) {
    issues.push(
      `${maybeSupportedCount} claim${maybeSupportedCount > 1 ? 's' : ''} maybe supported`,
    );
  }
  if (couldNotFindCount > 0) {
    issues.push(
      `${couldNotFindCount} claim${couldNotFindCount > 1 ? 's' : ''} could not find support`,
    );
  }
  if (supportedCount > 0 && issues.length === 0) {
    issues.push(
      `${supportedCount} claim${supportedCount > 1 ? 's' : ''} supported`,
    );
  }

  // Determine overall status
  // Paper not verified is always "issues"
  if (!canVerifyPaper) {
    return { status: 'issues', issues };
  }

  // Check claim support levels
  if (notSupportedCount > 0 || couldNotFindCount > 0) {
    return { status: 'issues', issues };
  }
  if (partiallySupportedCount > 0 || maybeSupportedCount > 0) {
    return { status: 'potential_issues', issues };
  }
  if (supportedCount === evaluations.length) {
    return {
      status: hasMetadataIssues ? 'potential_issues' : 'no_issues',
      issues,
    };
  }

  issues.push('Unable to verify claims');
  return { status: 'error', issues };
}

export const ReferenceCard: React.FC<ReferenceCardProps> = ({
  identifier,
  title,
  authors,
  publicationYear,
  publication,
  doi,
  citationCount,
  impactFactor,
  journalQuartile,
  claims,
  evaluations = [],
  isStreaming = false,
  fullTextUrl,
  bestSourceUrl,
  openalexId,
  rawReference,
  foundMetadata,
  metadataSource,
  validation,
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasContent =
    claims.length > 0 ||
    evaluations.length > 0 ||
    !!(validation && foundMetadata);

  // Match evaluations to claims and add source_url from fullTextUrl
  const claimsWithEvaluations = claims.map((claimText) => {
    const evaluation = evaluations.find((e) => e.claim_text === claimText);
    const evaluationWithSource = evaluation
      ? { ...evaluation, source_url: evaluation.source_url || fullTextUrl }
      : undefined;
    return {
      claimText,
      evaluation: evaluationWithSource,
    };
  });

  // Calculate reference status and issues
  const { status, issues } = calculateReferenceIssues(
    evaluations,
    claims,
    isStreaming,
    openalexId,
    fullTextUrl,
    bestSourceUrl,
    validation,
  );

  const statusLabels: Record<ReferenceStatus, string> = {
    no_issues: 'No Issue',
    potential_issues: 'Potential Issues',
    issues: 'Issues',
    loading: 'Loading',
    error: 'Error',
  };

  // Format identifier - normalize to [N] format
  const formattedIdentifier = identifier
    ? `[${identifier.replace(/^\[|\]$/g, '').replace(/[,.]$/, '')}]`
    : '';

  // Format reference text - prefer raw reference, fall back to formatted
  const referenceDisplayText = rawReference
    ? `${formattedIdentifier}${formattedIdentifier ? ' ' : ''}${rawReference}`
    : `${formattedIdentifier}${formattedIdentifier ? ' ' : ''}${title || 'Untitled Reference'}`;

  return (
    <CardContainer className={isStreaming ? 'streaming' : ''}>
      <CardHeader
        clickable={hasContent}
        onClick={() => hasContent && setExpanded(!expanded)}
      >
        <TitleRow>
          <BadgesRow>
            <StatusBadge status={status}>{statusLabels[status]}</StatusBadge>
            {issues.map((issue, index) => (
              <SummaryBadge key={index}>{issue}</SummaryBadge>
            ))}
          </BadgesRow>
          {hasContent && (
            <SeeDetailsLink
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? 'Hide details' : 'See details'}
            </SeeDetailsLink>
          )}
        </TitleRow>

        <ReferenceText>{referenceDisplayText}</ReferenceText>

        <InfoRow>
          {doi && (
            <InfoBadge
              href={`https://doi.org/${doi}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Icon name="link" size={12} />
              DOI
            </InfoBadge>
          )}
          {openalexId && (
            <InfoBadge
              href={`https://openalex.org/works/W${openalexId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Icon name="science" size={12} />
              OpenAlex
            </InfoBadge>
          )}
          {publicationYear && (
            <InfoBadge as="span">
              <Icon name="calendar_today" size={12} />
              {publicationYear}
            </InfoBadge>
          )}
          {citationCount !== null && citationCount !== undefined && (
            <InfoBadge as="span">
              <Icon name="format_quote" size={12} />
              {citationCount.toLocaleString()} citations
            </InfoBadge>
          )}
        </InfoRow>

        <JournalInfo>
          {publication && <span>{publication}</span>}
          {impactFactor && (
            <>
              {publication && <span> </span>}
              <span>IF: {impactFactor.toFixed(1)}</span>
            </>
          )}
          {journalQuartile && (
            <>
              {(publication || impactFactor) && <span> </span>}
              <span>{journalQuartile}</span>
            </>
          )}
        </JournalInfo>
      </CardHeader>

      <ClaimsSection expanded={expanded}>
        <ClaimsContainer>
          {/* Metadata Validation Section */}
          {validation && foundMetadata && (
            <MetadataSection>
              <Heading as="h5" size="xs" style={{ marginBottom: '12px' }}>
                Metadata Validation
              </Heading>
              <MetadataValidationRow>
                {validation.title_similarity !== null &&
                  validation.title_similarity !== undefined && (
                    <ValidationBadge
                      status={
                        validation.title_similarity_threshold_met
                          ? 'pass'
                          : 'warning'
                      }
                    >
                      <Icon
                        name={
                          validation.title_similarity_threshold_met
                            ? 'check'
                            : 'warning'
                        }
                        size={12}
                      />
                      Title: {(validation.title_similarity * 100).toFixed(0)}%
                    </ValidationBadge>
                  )}
                {validation.authors_match !== null &&
                  validation.authors_match !== undefined && (
                    <ValidationBadge
                      status={validation.authors_match ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={validation.authors_match ? 'check' : 'close'}
                        size={12}
                      />
                      Authors {validation.authors_match ? 'match' : 'mismatch'}
                    </ValidationBadge>
                  )}
                {validation.year_matches !== null &&
                  validation.year_matches !== undefined && (
                    <ValidationBadge
                      status={validation.year_matches ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={validation.year_matches ? 'check' : 'close'}
                        size={12}
                      />
                      Year {validation.year_matches ? 'matches' : 'mismatch'}
                    </ValidationBadge>
                  )}
                {validation.publication_matches !== null &&
                  validation.publication_matches !== undefined && (
                    <ValidationBadge
                      status={validation.publication_matches ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={
                          validation.publication_matches ? 'check' : 'close'
                        }
                        size={12}
                      />
                      Publication{' '}
                      {validation.publication_matches ? 'matches' : 'mismatch'}
                    </ValidationBadge>
                  )}
                {validation.doi_format_valid !== null &&
                  validation.doi_format_valid !== undefined && (
                    <ValidationBadge
                      status={validation.doi_format_valid ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={validation.doi_format_valid ? 'check' : 'close'}
                        size={12}
                      />
                      DOI {validation.doi_format_valid ? 'valid' : 'invalid'}
                    </ValidationBadge>
                  )}
                {validation.doi_resolves !== null &&
                  validation.doi_resolves !== undefined && (
                    <ValidationBadge
                      status={validation.doi_resolves ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={validation.doi_resolves ? 'check' : 'close'}
                        size={12}
                      />
                      DOI{' '}
                      {validation.doi_resolves
                        ? 'resolves'
                        : 'does not resolve'}
                    </ValidationBadge>
                  )}
                {validation.doi_matches !== null &&
                  validation.doi_matches !== undefined && (
                    <ValidationBadge
                      status={validation.doi_matches ? 'pass' : 'fail'}
                    >
                      <Icon
                        name={validation.doi_matches ? 'check' : 'close'}
                        size={12}
                      />
                      DOI {validation.doi_matches ? 'matches' : 'mismatch'}
                    </ValidationBadge>
                  )}
              </MetadataValidationRow>

              <MetadataGrid>
                <MetadataHeader>
                  <span></span>
                  <span>Citation</span>
                  <span>
                    {metadataSource === 'openalex'
                      ? 'Found on OpenAlex'
                      : 'Found on Web'}
                  </span>
                </MetadataHeader>

                {/* Title Row */}
                <MetadataLabel>Title</MetadataLabel>
                <MetadataValue
                  status={getValidationStatus(
                    validation.title_similarity_threshold_met,
                  )}
                >
                  {title || '—'}
                </MetadataValue>
                <MetadataValue
                  status={getValidationStatus(
                    validation.title_similarity_threshold_met,
                  )}
                >
                  {foundMetadata.title || '—'}
                </MetadataValue>

                {/* Authors Row */}
                <MetadataLabel>Authors</MetadataLabel>
                <MetadataValue
                  status={getValidationStatus(validation.authors_match)}
                >
                  {authors && authors.length > 0 ? authors.join(', ') : '—'}
                </MetadataValue>
                <MetadataValue
                  status={getValidationStatus(validation.authors_match)}
                >
                  {foundMetadata.authors && foundMetadata.authors.length > 0
                    ? foundMetadata.authors.join(', ')
                    : '—'}
                </MetadataValue>

                {/* Year Row */}
                <MetadataLabel>Year</MetadataLabel>
                <MetadataValue
                  status={getValidationStatus(validation.year_matches)}
                >
                  {publicationYear || '—'}
                </MetadataValue>
                <MetadataValue
                  status={getValidationStatus(validation.year_matches)}
                >
                  {foundMetadata.publication_year || '—'}
                </MetadataValue>

                {/* Publication Row */}
                <MetadataLabel>Publication</MetadataLabel>
                <MetadataValue
                  status={getValidationStatus(validation.publication_matches)}
                >
                  {publication || '—'}
                </MetadataValue>
                <MetadataValue
                  status={getValidationStatus(validation.publication_matches)}
                >
                  {foundMetadata.publication || '—'}
                </MetadataValue>

                {/* DOI Row */}
                <MetadataLabel>DOI</MetadataLabel>
                <MetadataValue
                  status={getDoiStatus(
                    doi,
                    foundMetadata.doi,
                    validation.doi_matches,
                  )}
                >
                  {doi ? (
                    <BodyLink
                      href={`https://doi.org/${doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {doi}
                    </BodyLink>
                  ) : (
                    '—'
                  )}
                </MetadataValue>
                <MetadataValue
                  status={getDoiStatus(
                    doi,
                    foundMetadata.doi,
                    validation.doi_matches,
                  )}
                >
                  {foundMetadata.doi ? (
                    <BodyLink
                      href={`https://doi.org/${foundMetadata.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="xs"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {foundMetadata.doi}
                    </BodyLink>
                  ) : (
                    '—'
                  )}
                </MetadataValue>
              </MetadataGrid>
            </MetadataSection>
          )}

          <ClaimsHeader>
            <Heading as="h5" size="xs">
              Claims that rely on this reference
            </Heading>
            {isStreaming && evaluations.length < claims.length && (
              <Body size="xs" color="tertiary">
                {evaluations.length} of {claims.length} evaluated
              </Body>
            )}
          </ClaimsHeader>

          {claimsWithEvaluations.length > 0 ? (
            <>
              {claimsWithEvaluations.map((item, index) => (
                <ClaimEvaluationRow
                  key={index}
                  index={index}
                  claimText={item.claimText}
                  evaluation={item.evaluation}
                  isLoading={isStreaming && !item.evaluation}
                />
              ))}
            </>
          ) : isStreaming ? (
            <>
              <SkeletonClaimRow>
                <SkeletonLine style={{ width: '30%' }} />
                <SkeletonLine style={{ width: '90%' }} />
              </SkeletonClaimRow>
              <SkeletonClaimRow>
                <SkeletonLine style={{ width: '25%' }} />
                <SkeletonLine style={{ width: '80%' }} />
              </SkeletonClaimRow>
            </>
          ) : (
            <NoClaimsMessage size="sm">
              No claims associated with this reference.
            </NoClaimsMessage>
          )}
        </ClaimsContainer>
      </ClaimsSection>
    </CardContainer>
  );
};

export default ReferenceCard;
